import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money, fmtDateShort, PAYMENT_METHODS } from '@/lib/helpers';
import { Plus, Pencil, Trash2 } from 'lucide-react';

// Local calendar date (YYYY-MM-DD) for today — avoids UTC off-by-one on the picker.
const todayStr = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

// Combine a selected calendar date (YYYY-MM-DD) with a time-of-day. For edits we
// keep the expense's original time; for new entries we use the current time — so
// only the calendar date changes and ordering/reports stay correct.
const buildIsoDate = (ymd, refIso) => {
  const t = (typeof refIso === 'string' && refIso.length > 10) ? refIso.slice(10) : new Date().toISOString().slice(10);
  return `${ymd}T${t}`;
};

const BLANK = { category: 'Rent', amount: '', payment_method: 'CASH', paid_to: '', store_id: '', notes: '', date: todayStr() };

export default function Expenses() {
  const { stores } = useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // expense id being edited, or null for add
  const [form, setForm] = useState(BLANK);
  const [origDate, setOrigDate] = useState(null); // original ISO date of the expense being edited
  const [delId, setDelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState('');

  const { data: expenses = [] } = useQuery({ queryKey: ['expenses'], queryFn: async () => (await api.get('/expenses')).data.data });
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: async () => (await api.get('/settings')).data.data });
  const { data: catRows = [] } = useQuery({ queryKey: ['exp-cats'], queryFn: async () => (await api.get('/expense-categories')).data.data });
  const cats = catRows.length ? catRows.map((c) => c.name) : (settings?.expense_categories || ['Rent', 'Salary', 'Utilities', 'Misc']);

  const refresh = () => qc.invalidateQueries({ queryKey: ['expenses'] });

  const openAdd = () => { setEditing(null); setOrigDate(null); setForm({ ...BLANK, date: todayStr() }); setAddingCat(false); setNewCat(''); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e.id);
    setOrigDate(e.date || null);
    setForm({ category: e.category || 'Rent', amount: String(e.amount ?? ''), payment_method: e.payment_method || 'CASH',
      paid_to: e.paid_to || '', store_id: e.store_id || '', notes: e.notes || '',
      date: (e.date && e.date.length >= 10) ? e.date.slice(0, 10) : todayStr() });
    setOpen(true);
  };

  const createCat = async () => {
    const name = newCat.trim();
    if (!name) { toast.error('Enter a category name.'); return; }
    setBusy(true);
    try {
      await api.post('/expense-categories', { name });
      await qc.invalidateQueries({ queryKey: ['exp-cats'] });
      toast.success('Category added');
    } catch (e) {
      // If it already exists, just select it silently.
      if (e?.response?.status !== 400 && e?.response?.status !== 409) { toast.error(errMsg(e)); setBusy(false); return; }
    }
    setForm((f) => ({ ...f, category: name }));
    setAddingCat(false); setNewCat('');
    setBusy(false);
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) < 0) { toast.error('Enter a valid amount.'); return; }
    if (!form.date) { toast.error('Select a date.'); return; }
    setBusy(true);
    try {
      // Keep original time-of-day on edits; use current time for new entries.
      const isoDate = buildIsoDate(form.date, editing ? origDate : null);
      const payload = { category: form.category, amount: Number(form.amount), payment_method: form.payment_method,
        paid_to: form.paid_to, store_id: form.store_id, notes: form.notes, date: isoDate };
      if (editing) {
        await api.patch(`/expenses/${editing}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', payload);
        toast.success('Expense added');
      }
      setOpen(false); setEditing(null); setOrigDate(null); setForm({ ...BLANK, date: todayStr() }); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/expenses/${delId}`); toast.success('Expense deleted'); setDelId(null); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const storeName = (id) => stores.find((s) => s.id === id)?.name;

  return (
    <Page>
      <TopBar title="Expenses" showStore={false} right={<Button size="sm" className="h-9 rounded-full" onClick={openAdd} data-testid="expense-add"><Plus className="h-4 w-4" /></Button>} />
      <div className="space-y-2 px-4 pt-4">
        {expenses.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No expenses yet.</p> :
          expenses.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-3" data-testid={`expense-${e.id}`}>
              <button className="min-w-0 flex-1 text-left" onClick={() => openEdit(e)} data-testid={`expense-edit-${e.id}`}>
                <p className="truncate text-sm font-medium">{e.category}</p>
                <p className="truncate text-xs text-muted-foreground">{e.paid_to || '\u2014'} · {e.payment_method} · {storeName(e.store_id) || 'Common'} · {fmtDateShort(e.date)}</p>
              </button>
              <p className="font-mono text-sm font-semibold">{money(e.amount)}</p>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-8 w-8 rounded-lg p-0" onClick={() => openEdit(e)} aria-label="Edit expense" data-testid={`expense-edit-btn-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 rounded-lg p-0 text-destructive" onClick={() => setDelId(e.id)} aria-label="Delete expense" data-testid={`expense-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
      </div>

      <Drawer open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setOrigDate(null); } }}>
        <DrawerContent className="bg-card">
          <div className="mx-auto my-2 h-1 w-10 rounded-full bg-border" />
          <DrawerHeader><DrawerTitle>{editing ? 'Edit expense' : 'Add expense'}</DrawerTitle></DrawerHeader>
          <div className="space-y-3 px-4 pb-6">
            <div>
              <div className="flex items-center justify-between">
                <Label>Category</Label>
                <button type="button" data-testid="expense-add-category-toggle"
                  onClick={() => { setAddingCat((v) => !v); setNewCat(''); }}
                  className="text-xs font-semibold text-primary hover:underline">{addingCat ? 'Cancel' : '+ New category'}</button>
              </div>
              {addingCat ? (
                <div className="mt-1 flex gap-2">
                  <Input value={newCat} onChange={(e) => setNewCat(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createCat(); } }}
                    placeholder="New category name" autoFocus className="h-11 rounded-xl" data-testid="expense-new-category-input" />
                  <Button type="button" onClick={createCat} disabled={busy} className="h-11 shrink-0 rounded-xl" data-testid="expense-new-category-save">Add</Button>
                </div>
              ) : (
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="expense-category"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent></Select>
              )}
            </div>
            <div><Label>Amount</Label><Input inputMode="numeric" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="expense-amount" /></div>
            <div><Label htmlFor="expense-date">Date</Label><Input id="expense-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="expense-date" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Payment mode</Label><Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}><SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="expense-method"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.filter((m) => m !== 'CREDIT').map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent></Select></div>
              <div><Label>Store</Label><Select value={form.store_id || 'none'} onValueChange={(v) => setForm({ ...form, store_id: v === 'none' ? '' : v })}><SelectTrigger className="mt-1 h-11 rounded-xl"><SelectValue placeholder="Common" /></SelectTrigger><SelectContent><SelectItem value="none">Common</SelectItem>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent></Select></div>
            </div>
            <div><Label>Paid to</Label><Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="expense-paidto" /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="expense-notes" /></div>
            <Button className="h-12 w-full rounded-xl" onClick={save} disabled={busy} data-testid="expense-save">{editing ? 'Update' : 'Save'}</Button>
          </div>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={!!delId} onOpenChange={(o) => { if (!o) setDelId(null); }}>
        <AlertDialogContent data-testid="expense-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the expense entry. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={busy} data-testid="expense-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
